import { For, Show, type JSX } from "solid-js";

import { Icon, type IconName } from "~/icon";
import clsx from "~/util/html/style";

import { ProcedureArt } from "../art/procedure_art";
import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import { PracticePicture } from "../practice_picture";
import type { FamilySlide as FamilySlideData, PictureSrc } from "../types";

// The library holds each row to one sentence of 95 characters. A deck composed before it did carries paragraphs.
const BRIEF_ROW_CHARS = 100;

/** One treatment from Twelve's library: the drawing (or the practice's own picture in its place), what it is, why it's
 *  needed, what to expect, and where. Rows of one short sentence are set larger; a copy frozen with the earlier
 *  paragraphs keeps the size they fit at. */
export function FamilySlide(props: {
  slide: FamilySlideData;
  pictureSrc: PictureSrc;
}): JSX.Element {
  const words = useDeckWords();
  const brief = () =>
    [props.slide.what, props.slide.why, props.slide.expect].every(
      (row) => row.length <= BRIEF_ROW_CHARS,
    );
  return (
    <>
      <div class={styles["pr-panel"]}>
        <Show
          when={props.slide.practicePicture}
          fallback={<ProcedureArt art={props.slide.art} title={props.slide.title} />}
        >
          {(page) => (
            <PracticePicture
              page={page()}
              pictureSrc={props.pictureSrc}
              alt={props.slide.title}
              class={styles["pr-photo"]}
              fallback={<ProcedureArt art={props.slide.art} title={props.slide.title} />}
            />
          )}
        </Show>
      </div>
      <div class={styles["pr-text"]} data-brief={brief() ? "" : undefined}>
        <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
        <Row icon="info" label={words().t("whatItIs")} text={props.slide.what} />
        <Row icon="heart" label={words().t("whyNeeded")} text={props.slide.why} />
        <Row icon="calendar" label={words().t("whatToExpect")} text={props.slide.expect} />
        <ul class={styles["where"]}>
          <For each={props.slide.sites}>{(site) => <li class={styles["chip"]}>{site}</li>}</For>
          <Show when={props.slide.visits.length > 0}>
            <li class={clsx(styles["chip"], styles["chip-v"])}>
              {words().visitsPhrase(props.slide.visits)}
            </li>
          </Show>
        </ul>
      </div>
    </>
  );
}

function Row(props: { icon: IconName; label: string; text: string }) {
  return (
    <div class={styles["pr-row"]}>
      <span class={styles["pr-ico"]}>
        <Icon name={props.icon} size="none" aria-hidden="true" />
      </span>
      <div>
        <div class={styles["pr-k"]}>{props.label}</div>
        <p class={styles["pr-v"]}>{props.text}</p>
      </div>
    </div>
  );
}
